/**
 * Thin client functions for the basket checkout pipeline.
 * Used by consumers (e.g. youmo) that need direct control over the
 * createBasketCheckoutSession → processBasketPayment / confirmFreeBasketOrder
 * flow rather than delegating to the heavier confirmFreeBooking orchestrator.
 *
 * Endpoint paths mirror the app router:
 *   POST /app/createBasketCheckoutSession
 *   POST /app/processBasketPayment
 *   POST /app/confirmFreeBasketOrder
 *   GET  /app/basketOrderStatus/:basketOrderId
 */

import axios, { AxiosError } from "axios";
import type { CheckoutApiConfig, SourceInfo } from "./types";
import { getSourceInfo } from "./api";

const DEFAULT_API_URL = "https://api.baluu.io";

/**
 * Hard ceiling on every basket API request. axios has NO default timeout, so
 * without this a stalled connection (API unreachable, hung proxy, slow Stripe
 * round-trip that never returns) leaves the request pending forever — which is
 * exactly what froze the youmo checkout spinner with no error and no escape.
 * With a timeout the request rejects, the hook lands in `status: "error"`
 * (create) or falls through to polling (best-effort processBasketPayment), so
 * the spinner can never be infinite. 30s is generous enough not to false-trip
 * a slow-but-working create (PaymentIntent + DB writes).
 */
const BASKET_REQUEST_TIMEOUT_MS = 30_000;

function getBaseUrl(config: CheckoutApiConfig): string {
  return config.baseUrl || DEFAULT_API_URL;
}

// ---------------------------------------------------------------------------
// Typed error class
// ---------------------------------------------------------------------------

/**
 * Thrown by all four basket API functions on any HTTP failure.
 *
 * - `message`  — human-readable string (same text as the old plain Error).
 * - `code`     — machine-readable server code when present (e.g.
 *                `YOUMO_SINGLE_ITEM_ONLY`, `HOLD_FLOW_SINGLE_ITEM_ONLY`,
 *                `BASKET_DISABLED_SINGLE_ITEM_ONLY`).
 * - `status`   — HTTP status code (e.g. 400 = validation error,
 *                409 = seats taken mid-checkout — retryable with another date).
 * - `details`  — full parsed response body (e.g. `unavailableItems` on 409).
 */
export class BasketApiError extends Error {
  code?: string;
  status?: number;
  details?: unknown;

  constructor(
    message: string,
    opts?: { code?: string; status?: number; details?: unknown },
  ) {
    super(message);
    this.name = "BasketApiError";
    this.code = opts?.code;
    this.status = opts?.status;
    this.details = opts?.details;
  }
}

// ---------------------------------------------------------------------------
// Shared error helper
// ---------------------------------------------------------------------------

type BasketServerBody = {
  message?: string;
  error?: string;
  code?: string;
  [key: string]: unknown;
};

/**
 * Extracts the axios response body, calls `config.onError` if set, then
 * throws a `BasketApiError`. Text precedence for `.message` is:
 *   data.message > data.error > axios message > fallbackMessage
 * — identical to the previous per-function catch blocks so display behaviour
 * is unchanged for callers that only read `.message`.
 */
function handleBasketApiError(
  error: unknown,
  config: CheckoutApiConfig,
  context: { action: string; [k: string]: unknown },
  fallbackMessage: string,
): never {
  const axiosError = error as AxiosError<BasketServerBody>;
  const data = axiosError.response?.data;
  const status = axiosError.response?.status;

  // A request that hit BASKET_REQUEST_TIMEOUT_MS (or was otherwise aborted)
  // has no response body — give it a clear, retryable message instead of
  // axios's raw "timeout of 30000ms exceeded".
  const isTimeout = axiosError.code === "ECONNABORTED";

  const message = isTimeout
    ? "The request timed out. Please check your connection and try again."
    : data?.message ||
      data?.error ||
      axiosError.message ||
      fallbackMessage;

  if (config.onError) {
    config.onError(new Error(message), context);
  }

  throw new BasketApiError(message, {
    code: data?.code,
    status,
    details: data,
  });
}

// ---------------------------------------------------------------------------
// Input types
// ---------------------------------------------------------------------------

export type BasketTicketSelection = {
  ticketTypeId: string;
  ticketTypeName: string;
  quantity: number;
  price: number;
  numberOfGuestsPerTicket?: number;
};

export type BasketAddOn = {
  addOnId: string;
  addOnName: string;
  quantity: number;
  price: number;
};

export type BasketItemInput = {
  /** Client-generated UUID for this line item */
  id: string;
  type: "booking" | "product" | "giftcard" | "bundle";
  bookingType?: "regular" | "dropIn" | "trial" | "term" | "appointment";
  schoolId: string;
  courseId?: string;
  productId?: string;
  sessionId?: string;
  ticketSelections?: BasketTicketSelection[];
  addOns?: BasketAddOn[];
  quantity: number;
  price: number;
  currency: string;
  courseName?: string;
  productName?: string;
  sessionDate?: string;
  sessionEndDate?: string;
  imageUrl?: string;
  appointmentData?: {
    startTime: string;
    endTime: string;
    duration: number;
  };
  termData?: {
    termId: string;
    termName: string;
    isProRata: boolean;
    sessionsIncluded: number;
    startDate: string;
    endDate: string;
  };
  isOnline?: boolean;
  [key: string]: unknown;
};

export type BasketBuyer = {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  userId?: string;
  marketingOptIn?: boolean;
};

// ---------------------------------------------------------------------------
// Shared linked-record type
// ---------------------------------------------------------------------------

export type BasketLinkedRecord = {
  id: string;
  basketItemId: string | null;
  recordType: string;
  recordId: string | null;
  processingStatus: string;
  processingError: string | null;
  inngestRunId: string | null;
  processedAt: string | null;
};

// ---------------------------------------------------------------------------
// createBasketCheckoutSession
// ---------------------------------------------------------------------------

export type CreateBasketCheckoutSessionInput = {
  items: BasketItemInput[];
  buyer: BasketBuyer;
  discountCode?: string;
  returnUrl?: string;
};

/**
 * Response shape from POST /app/createBasketCheckoutSession.
 *
 * Free-order early-return in CreateBasketCheckoutSessionController:
 *   amount === 0, clientSecret === null, checkoutUrl === null
 *
 * Paid-order response from CreateBasketCheckoutSessionController:
 *   amount > 0, clientSecret or checkoutUrl populated (depends on hosted page)
 *
 * Hook callers: branch on `amount === 0` to decide whether to collect payment.
 */
export type CreateBasketCheckoutSessionResult = {
  basketOrderId: string;
  purchaseId: string;
  paymentId: string | null;
  clientSecret: string | null;
  successUrl: string | null;
  usedStripeHostedPage: boolean;
  checkoutUrl: string | null;
  schoolAccountID: string | undefined;
  stripeAccountId: string | undefined;
  stripePublicKey?: string;
  paymentMethodType: string | null;
  amount: number;
  currency: string;
  itemCount: number;
  applicationFeeAmount: number;
  commissionPercentage: number;
  /**
   * Buyer-side service fee already folded into `amount` (Task 6/7). Always 0
   * on non-Baluu-hosted surfaces (webcomponent/iframe embeds), free orders,
   * and schools with no fee configured — render the "Service fee" line item
   * only when this is > 0.
   */
  serviceFeePence: number;
  /**
   * School-set booking fee (customer-paid), already folded into `amount`.
   * Always 0 on free orders and schools with no booking fee configured —
   * render the "Booking fee" line item only when this is > 0.
   */
  bookingFeePence?: number;
  /**
   * True when every item is a request-to-book ticket: the order collects no
   * money now (`clientSecret` null, `serviceFeePence` 0) and is confirmed via
   * confirmFreeBasketOrder as a request. Absent on older APIs.
   */
  isRequestToBook?: boolean;
  /**
   * A request whose deposit is collected NOW, on this session's card form
   * (`clientSecret` / hosted URL set). Sent WITHOUT `isRequestToBook` so
   * bundles that predate it take the card path; never confirmed via
   * confirmFreeBasketOrder — see `isUnpaidRequestResponse` /
   * `isDepositRequestResponse`.
   */
  requestDeposit?: boolean;
};

/**
 * Creates a basket checkout session.
 * Source and sourceInfo are derived from config (matching the confirmFreeBooking
 * convention: config.source || (isWebcomponent ? "webcomponent" : "widget")).
 *
 * @param data   - Items, buyer, optional discount code and returnUrl.
 * @param config - Shared checkout API config (baseUrl, source, sourceInfo, …).
 */
export async function createBasketCheckoutSession(
  data: CreateBasketCheckoutSessionInput,
  config: CheckoutApiConfig,
): Promise<CreateBasketCheckoutSessionResult> {
  const baseUrl = getBaseUrl(config);
  const source =
    config.source || (config.isWebcomponent ? "webcomponent" : "widget");
  const sourceInfo: SourceInfo =
    config.sourceInfo || getSourceInfo(config.isWebcomponent);

  try {
    const response = await axios.post<CreateBasketCheckoutSessionResult>(
      `${baseUrl}/app/createBasketCheckoutSession`,
      {
        items: data.items,
        buyer: data.buyer,
        discountCode: data.discountCode,
        returnUrl: data.returnUrl,
        source,
        sourceInfo,
      },
      { timeout: BASKET_REQUEST_TIMEOUT_MS },
    );
    return response.data;
  } catch (error) {
    return handleBasketApiError(
      error,
      config,
      { action: "createBasketCheckoutSession", source },
      "Failed to create basket checkout session",
    );
  }
}

// ---------------------------------------------------------------------------
// processBasketPayment
// ---------------------------------------------------------------------------

export type ProcessBasketPaymentInput = {
  basketOrderId: string;
  /** The Stripe PaymentIntent id (pi_…) or CheckoutSession id (cs_…). */
  paymentIntentId?: string;
  /** Explicit checkout session id when known separately. */
  checkoutSessionId?: string;
  /** Stripe charge id, if available. */
  chargeId?: string;
};

export type ProcessBasketPaymentResult = {
  success: boolean;
  basketOrderId: string;
  message: string;
  paymentStatus?: string;
  linkedRecords: BasketLinkedRecord[];
};

/**
 * Notifies the backend that the customer has completed Stripe payment.
 * The controller resolves the real paymentIntentId from a checkout session
 * if needed and triggers Inngest processing as a fallback for slow webhooks.
 *
 * Body fields read by the controller:
 *   basketOrderId, paymentIntentId, checkoutSessionId, chargeId
 */
export async function processBasketPayment(
  data: ProcessBasketPaymentInput,
  config: CheckoutApiConfig,
): Promise<ProcessBasketPaymentResult> {
  const baseUrl = getBaseUrl(config);

  try {
    const response = await axios.post<ProcessBasketPaymentResult>(
      `${baseUrl}/app/processBasketPayment`,
      {
        basketOrderId: data.basketOrderId,
        paymentIntentId: data.paymentIntentId,
        checkoutSessionId: data.checkoutSessionId,
        chargeId: data.chargeId,
      },
      { timeout: BASKET_REQUEST_TIMEOUT_MS },
    );
    return response.data;
  } catch (error) {
    return handleBasketApiError(
      error,
      config,
      { action: "processBasketPayment", basketOrderId: data.basketOrderId },
      "Failed to process basket payment",
    );
  }
}

// ---------------------------------------------------------------------------
// confirmFreeBasketOrder
// ---------------------------------------------------------------------------

export type ConfirmFreeBasketOrderResult = {
  success: boolean;
  basketOrderId: string;
  message: string;
  linkedRecordIds?: string[];
  itemsProcessing?: number;
};

/**
 * Mode 1 confirm: confirms an EXISTING zero-amount basket order that was
 * already created by createBasketCheckoutSession.
 *
 * This is a thin wrapper around Mode 1 of ConfirmFreeBasketOrderController
 * (the free-order early-return branch: `if (existingOrderId && !bundleId && !membershipSubscriptionId)`).
 * It sends only `{ basketOrderId }` — do not pass items/buyer/bundleId here.
 *
 * NOTE: this is NOT the same as api.ts confirmFreeBooking, which is a heavier
 * orchestrator that creates-then-confirms in a single call. This function is
 * the thin confirm-only step for callers that already have a basketOrderId.
 *
 * @param basketOrderId - The id returned by createBasketCheckoutSession.
 * @param config        - Shared checkout API config.
 */
export async function confirmFreeBasketOrder(
  basketOrderId: string,
  config: CheckoutApiConfig,
): Promise<ConfirmFreeBasketOrderResult> {
  const baseUrl = getBaseUrl(config);

  try {
    const response = await axios.post<ConfirmFreeBasketOrderResult>(
      `${baseUrl}/app/confirmFreeBasketOrder`,
      { basketOrderId },
      { timeout: BASKET_REQUEST_TIMEOUT_MS },
    );
    return response.data;
  } catch (error) {
    return handleBasketApiError(
      error,
      config,
      { action: "confirmFreeBasketOrder", basketOrderId },
      "Failed to confirm free basket order",
    );
  }
}

// ---------------------------------------------------------------------------
// getBasketOrderStatus
// ---------------------------------------------------------------------------

export type BasketOrderProcessingStats = {
  total: number;
  pending: number;
  processing: number;
  completed: number;
  failed: number;
};

export type GetBasketOrderStatusResult = {
  success: boolean;
  basketOrderId: string;
  paymentStatus: string;
  processingStats: BasketOrderProcessingStats;
  allItemsCompleted: boolean;
  linkedRecords: BasketLinkedRecord[];
};

/**
 * Fetches the processing status of a basket order.
 * Route: GET /app/basketOrderStatus/:basketOrderId.
 *
 * Use processingStats.completed === processingStats.total to determine
 * whether all Inngest item events have finished. For single-item baskets
 * (youmo), allItemsCompleted is the primary readiness signal.
 *
 * NOTE: throws on any HTTP failure. For resilient polling use
 * `pollBasketOrderRecord` (which tolerates transient errors).
 *
 * @param basketOrderId - The basket order id.
 * @param config        - Shared checkout API config.
 */
export async function getBasketOrderStatus(
  basketOrderId: string,
  config: CheckoutApiConfig,
): Promise<GetBasketOrderStatusResult> {
  const baseUrl = getBaseUrl(config);

  try {
    const response = await axios.get<GetBasketOrderStatusResult>(
      `${baseUrl}/app/basketOrderStatus/${basketOrderId}`,
      { timeout: BASKET_REQUEST_TIMEOUT_MS },
    );
    return response.data;
  } catch (error) {
    return handleBasketApiError(
      error,
      config,
      { action: "getBasketOrderStatus", basketOrderId },
      "Failed to get basket order status",
    );
  }
}
